/**
 * Settings ▸ SDMS ▸ SDMS Template Builder — the legacy list
 * (SpreadSheetBuilder_TemplateInfo list view): one row per template + linked test,
 * with the legacy bar New · Refresh · Export to and a tick column.
 *
 * New and opening a row do NOT get an editor of their own: they open the ONE
 * template editor the app already has (Toolbox ▸ SDMS Template Builder,
 * `SdmsTemplateEditor`) — the tabbed builder over the same SpreadSheetBuilder_*
 * tables. A second editor here would be a second writer of those tables
 * (standing rule 8).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, ExportMenu } from '../../components/AppToolbar';
import '../modules/module.css';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useNavPermission } from '../../hooks/useNavPermission';
import { errorText, sdmsTemplateApi, type Lookups } from '../toolbox/sdmsTemplateBuilder/sdmsTemplateApi';
import SdmsTemplateEditor from '../toolbox/sdmsTemplateBuilder/SdmsTemplateEditor';
import { FORM_NAME } from '../toolbox/sdmsTemplateBuilder/templateConstants';

const PAGE_ID = 'set-sdms-template-builder';

interface Row {
  /** `TemplateID|Test|Method|Matrix` — one row per template + linked test. */
  oid: string;
  templateName: string;
  test: string;
  method: string;
  matrix: string;
  templateType: string;
  source: string;
}

const COLUMNS: AppColumn<Row>[] = [
  { field: 'templateName', header: 'TemplateName', width: 220 },
  { field: 'test', header: 'Test', width: 220 },
  { field: 'method', header: 'Method', width: 180 },
  { field: 'matrix', header: 'Matrix', width: 120 },
  { field: 'templateType', header: 'TemplateType', width: 150 },
  { field: 'source', header: 'Source', width: 130 },
];

const str = (v: unknown) => (v == null ? '' : String(v));
const templateIdOf = (oid: string) => Number.parseInt(oid.split('|')[0] ?? '', 10) || 0;

export function SdmsTemplateBuilderListPage({ onHome }: { onHome: () => void }) {
  const notify = useNotify();
  const perm = useNavPermission('SDMS');

  const [rows, setRows] = useState<Row[]>([]);
  const [lookups, setLookups] = useState<Lookups | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  // Page-level maximize (ModuleListView pattern): hides the breadcrumb, title and toolbar, keeps the search row
  // with Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setGridMax(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gridMax]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  /** null = list; 0 = new template; n = template n open in the editor */
  const [editing, setEditing] = useState<number | null>(null);
  /** How the open template starts: a row click views it; New, and a template the editor itself
   *  opens (after Save As / the first save of a new one), are editable straight away. */
  const [openMode, setOpenMode] = useState<'view' | 'edit'>('view');

  const load = useCallback(async () => {
    const started = performance.now();
    setLoading(true);
    try {
      // The whole list in one read (61 rows on the demo DB) — the grid pages it.
      const res = await api.get<{ data?: Record<string, unknown>[] }>(
        `/api/v1/modules/${PAGE_ID}`, { params: { page: 1, pageSize: 2000 } });
      setRows((res.data?.data ?? []).map((d) => ({
        oid: str(d.oid), templateName: str(d.templateName), test: str(d.test), method: str(d.method),
        matrix: str(d.matrix), templateType: str(d.templateType), source: str(d.source),
      })));
      setSelected(new Set());
    } catch (e) {
      notify.error(errorText(e, 'Could not load the templates.'));
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - started);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    // The editor's field catalog — fetched once, as the Toolbox list does.
    sdmsTemplateApi.lookups().then(setLookups)
      .catch((e) => notify.error(errorText(e, 'Could not load the field catalog.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.templateName, r.test, r.method, r.matrix, r.templateType, r.source].some((v) => v.toLowerCase().includes(q)));
  }, [rows, search]);

  if (editing !== null) {
    return (
      <SdmsTemplateEditor
        key={editing}
        id={editing || null}
        lookups={lookups}
        onHome={onHome}
        onClose={() => { setEditing(null); void load(); }}
        onSaved={() => void load()}
        onOpen={(id) => { setOpenMode('edit'); setEditing(id); }}
        legacyTabs
        initialMode={openMode}
      />
    );
  }

  return (
    <div className={`bl-page mlv-host${gridMax ? ' page-fullgrid' : ''}`}>
      <div className="bl-grid-area">
        {!gridMax && (<>
        <div className="dp-breadcrumb">
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          <span>SDMS</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">SDMS Template Builder</span>
        </div>

        <FormTitle pageId={PAGE_ID} loadMs={loadMs} loading={loading} auditFormName={FORM_NAME} />

        {/* Legacy bar: New · Refresh · Export to — in the ModuleListView toolbar row, so the search row below
            keeps the standard 8px gap (owner request 2026-10-14). */}
        <div className="mlv-toolbar-row">
        <AppToolbar
          onNew={perm.canWrite ? () => { setOpenMode('edit'); setEditing(0); } : undefined}
          onRefresh={() => void load()}
          refreshing={loading}
          exportData={{ filename: 'SDMS Template Builder', ...gridExportMatrix(shown, COLUMNS) }}
        />
        </div>
        </>)}

        {/* Search + Export + Maximize on ONE row above the grid (ModuleListView layout, owner request 2026-10-14) —
            stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input type="text" placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="SDMS Template Builder" {...gridExportMatrix(shown, COLUMNS)} />
            <button type="button" className={`dv-gridact${gridMax ? ' dv-on' : ''}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? 'Restore' : 'Maximize'}>
              <i className={`ti ti-arrows-${gridMax ? 'minimize' : 'maximize'}`} />
            </button>
          </div>
        </div>

        <AppDataGrid<Row>
          rows={shown}
          columns={COLUMNS}
          getRowId={(r) => r.oid}
          loading={loading}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          // A single click opens the template READ-ONLY; its Edit button turns editing on (2026-10-13).
          onRowClick={(r) => { const id = templateIdOf(r.oid); if (id > 0) { setOpenMode('view'); setEditing(id); } }}
          defaultPageSize={20}
          hideHeaderTools
          emptyText="No SDMS templates"
        />
      </div>
    </div>
  );
}
